import {
  CreateAppointmentInput,
  CreateEncounterInput,
  CreatePatientInput,
  LocationInput,
  ProductType,
  RequisitionInput,
} from 'khshared/API';
import findActiveServiceRegionOrNull from 'khshared/findActiveServiceRegionOrNull';
import findMetroOrNull from 'khshared/findMetroOrNull';
import getPartnerConfig from 'khshared/metadata/getPartnerConfig';
import { v4 as uuidv4 } from 'uuid';

import KHAuth from '../../utils/KHAuth';
import {
  UnexpectedWizardActionError,
  Wizard,
  WizardContext,
  WizardState,
} from '../../utils/useWizardState';

export interface BookingWizardContext extends WizardContext {
  outstandingRequisitions: RequisitionInput[];
  appointment: Partial<CreateAppointmentInput>;
  encounters: Partial<CreateEncounterInput>[];
  patients: Partial<CreatePatientInput>[];
  primaryProductType: ProductType;
  patientsAmount: 'single' | 'multiple';
  coordinatorIsPatient: boolean;
}

export interface BookingRedirectState extends WizardState {
  name: 'booking-redirect-state';
}

export interface BookingSelectionState extends WizardState {
  name: 'booking-selection-state';
}

export interface BookingRedirectFailureState extends WizardState {
  name: 'booking-redirect-failure-state';
}

export interface OutOfServiceRegionEmailConfirmState extends WizardState {
  name: 'out-of-service-region-email-confirm-state';
  props: { location: LocationInput };
}

export interface OutOfServiceRegionEmailSuccessState extends WizardState {
  name: 'out-of-service-region-email-success-state';
}

export interface CoordinatorRoleState extends WizardState {
  name: 'coordinator-role-state';
}

export interface AppointmentLocationState extends WizardState {
  name: 'appointment-location-state';
}

export interface AppointmentServicesState extends WizardState {
  name: 'appointment-services-state';
}

export interface AppointmentPriceEstimateState extends WizardState {
  name: 'appointment-price-estimate-state';
}

export interface ScheduleAppointmentState extends WizardState {
  name: 'schedule-appointment-state';
}

export interface BookingSignInState extends WizardState {
  name: 'booking-signin-state';
}

export interface AppointmentPatientsState extends WizardState {
  name: 'appointment-patients-state';
}

export interface PatientInfoState extends WizardState {
  name: 'patient-info-state';
  props: { patientID: string };
}

export interface MedicareScreenerState extends WizardState {
  name: 'medicare-screener-state';
  props: {
    encounterID?: string | null | undefined;
    isPatient: boolean;
    patientName: string | null | undefined;
  };
}

export interface MedicareEligibilityStatusState extends WizardState {
  name: 'medicare-eligibility-status-state';
  props: {
    isPatientHomebound: boolean | undefined;
    isPatientInNursingFacility: boolean | undefined;
    isPatient: boolean;
    encounterID?: string | null | undefined;
    patientName: string | null | undefined;
  };
}

export interface MedicarePhotoState extends WizardState {
  name: 'medicare-photo-state';
  props: {
    encounterID?: string | null | undefined;
    isPatient?: boolean;
  };
}

export interface MedicareInsuranceIDState extends WizardState {
  name: 'medicare-insurance-id-state';
  props: {
    encounterID?: string | null | undefined;
    isPatient?: boolean;
  };
}

export interface InsurancePhotoState extends WizardState {
  name: 'insurance-photo-state';
  props: { encounterID?: string | null | undefined };
}

export interface RequisitionState extends WizardState {
  name: 'requisition-state';
  props: { encounterID?: string | null | undefined };
}

export interface EncounterCOVID19QuestionsState extends WizardState {
  name: 'encounter-covid-19-questions-state';
  props: { encounterID?: string | null | undefined };
}

export interface COVID19ForUsState extends WizardState {
  name: 'covid-19-for-us-state';
}

export interface AppointmentNotesState extends WizardState {
  name: 'appointment-notes-state';
}

export interface AppointmentPrepState extends WizardState {
  name: 'appointment-prep-state';
}

export interface AppointmentBillingState extends WizardState {
  name: 'appointment-billing-state';
}

export interface ChooseNewTimeState extends WizardState {
  name: 'choose-new-time-state';
}

export interface YoureBookedState extends WizardState {
  name: 'youre-booked-state';
}

export type BookingWizardState =
  | BookingRedirectState
  | BookingSelectionState
  | BookingRedirectFailureState
  | CoordinatorRoleState
  | AppointmentLocationState
  | AppointmentServicesState
  | AppointmentPriceEstimateState
  | OutOfServiceRegionEmailConfirmState
  | OutOfServiceRegionEmailSuccessState
  | ScheduleAppointmentState
  | BookingSignInState
  | COVID19ForUsState
  | AppointmentPatientsState
  | PatientInfoState
  | MedicareScreenerState
  | MedicareEligibilityStatusState
  | MedicarePhotoState
  | MedicareInsuranceIDState
  | InsurancePhotoState
  | RequisitionState
  | EncounterCOVID19QuestionsState
  | AppointmentNotesState
  | AppointmentPrepState
  | AppointmentBillingState
  | ChooseNewTimeState
  | YoureBookedState;

export type BookingWizardAction =
  | {
      name: 'redirect-action';
      wizardState: BookingRedirectState;
    }
  | {
      name: 'show-booking-selection-action';
      wizardState: BookingRedirectState | BookingSignInState;
    }
  | {
      name: 'show-booking-redirect-failure-action';
      wizardState: BookingRedirectState | BookingSignInState;
    }
  | {
      name: 'booking-selection-continue-action';
      wizardState: BookingSelectionState;
    }
  | {
      name: 'out-of-service-region-email-action';
      wizardState: AppointmentLocationState;
      props: { location: LocationInput };
    }
  | {
      name: 'out-of-service-region-email-continue-action';
      wizardState: OutOfServiceRegionEmailConfirmState;
    }
  | {
      name: 'coordinator-role-continue-action';
      wizardState: CoordinatorRoleState;
    }
  | {
      name: 'appointment-location-continue-action';
      wizardState: AppointmentLocationState;
    }
  | {
      name: 'appointment-services-continue-action';
      wizardState: AppointmentServicesState;
    }
  | {
      name: 'appointment-price-estimate-continue-action';
      wizardState: AppointmentPriceEstimateState;
    }
  | {
      name: 'schedule-appointment-continue-action';
      wizardState: ScheduleAppointmentState;
    }
  | {
      name: 'booking-signin-continue-action';
      wizardState: BookingSignInState;
    }
  | {
      name: 'appointment-patients-add-patient-action';
      wizardState: AppointmentPatientsState;
    }
  | {
      name: 'appointment-patients-select-patient-action';
      wizardState: AppointmentPatientsState;
      props: { patientID: string };
    }
  | {
      name: 'appointment-patients-continue-action';
      wizardState: AppointmentPatientsState;
    }
  | {
      name: 'patient-info-continue-action';
      wizardState: PatientInfoState;
      props: { isPatient: boolean; encounterID?: string | null | undefined; patientName: string };
    }
  | {
      name: 'medicare-screener-continue-action';
      wizardState: MedicareScreenerState;
      props: {
        isPatientHomebound: boolean | undefined;
        isPatientInNursingFacility: boolean | undefined;
        encounterID?: string | null | undefined;
        isPatient: boolean;
        patientName: string | null | undefined;
      };
    }
  | {
      name: 'medicare-eligibility-status-continue-action';
      wizardState: MedicareEligibilityStatusState;
      props: {
        isPatient: boolean;
        encounterID?: string | null | undefined;
      };
    }
  | {
      name: 'medicare-eligibility-status-reset-multiple-action';
      wizardState: MedicareEligibilityStatusState;
    }
  | {
      name: 'medicare-insurance-id-continue-action';
      wizardState: MedicareInsuranceIDState;
      props: {
        encounterID?: string | null | undefined;
        isPatient?: boolean;
      };
    }
  | {
      name: 'medicare-photo-continue-action';
      wizardState: MedicarePhotoState;
      props: {
        encounterID?: string | null | undefined;
        isPatient?: boolean;
      };
    }
  | {
      name: 'insurance-photo-continue-action';
      wizardState: InsurancePhotoState;
    }
  | {
      name: 'requisition-continue-action';
      wizardState: RequisitionState;
      props: { encounterID?: string | null | undefined };
    }
  | {
      name: 'encounter-covid-19-questions-continue-action';
      wizardState: EncounterCOVID19QuestionsState;
    }
  | {
      name: 'covid-19-for-us-continue-action';
      wizardState: COVID19ForUsState;
    }
  | {
      name: 'appointment-notes-continue-action';
      wizardState: AppointmentNotesState;
    }
  | {
      name: 'appointment-prep-continue-action';
      wizardState: AppointmentPrepState;
    }
  | {
      name: 'appointment-billing-continue-action';
      wizardState: AppointmentBillingState;
    }
  | {
      name: 'choose-another-time-action';
      wizardState: AppointmentBillingState;
    }
  | {
      name: 'choose-new-time-continue-action';
      wizardState: ChooseNewTimeState;
    };

function handleAction(
  action: BookingWizardAction,
  context: BookingWizardContext,
): BookingWizardState {
  switch (action.wizardState.name) {
    case 'booking-redirect-state':
      switch (action.name) {
        case 'show-booking-selection-action':
          return { name: 'booking-selection-state' };
        case 'show-booking-redirect-failure-action':
          return { name: 'booking-redirect-failure-state' };
        case 'redirect-action':
          return { name: 'coordinator-role-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'booking-selection-state':
      switch (action.name) {
        case 'booking-selection-continue-action':
          return { name: 'coordinator-role-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'coordinator-role-state':
      switch (action.name) {
        case 'coordinator-role-continue-action':
          return { name: 'appointment-location-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-location-state':
      switch (action.name) {
        case 'appointment-location-continue-action':
          return context.outstandingRequisitions.length > 0 ||
            context.appointment.partnerOrganization != null
            ? { name: 'schedule-appointment-state' }
            : { name: 'appointment-services-state' };
        case 'out-of-service-region-email-action':
          return { name: 'out-of-service-region-email-confirm-state', props: action.props };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'out-of-service-region-email-confirm-state':
      switch (action.name) {
        case 'out-of-service-region-email-continue-action':
          return { name: 'out-of-service-region-email-success-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-services-state':
      switch (action.name) {
        case 'appointment-services-continue-action':
          return { name: 'appointment-price-estimate-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-price-estimate-state':
      switch (action.name) {
        case 'appointment-price-estimate-continue-action':
          return { name: 'schedule-appointment-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'schedule-appointment-state':
      switch (action.name) {
        case 'schedule-appointment-continue-action':
          return KHAuth.isSignedIn()
            ? { name: 'patient-info-state', props: { patientID: KHAuth.getViewerIDOrThrow() } }
            : { name: 'booking-signin-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'booking-signin-state':
      switch (action.name) {
        case 'show-booking-selection-action':
          return { name: 'booking-selection-state' };
        case 'booking-signin-continue-action':
          return { name: 'patient-info-state', props: { patientID: KHAuth.getViewerIDOrThrow() } };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-patients-state':
      switch (action.name) {
        case 'appointment-patients-add-patient-action':
          return { name: 'patient-info-state', props: { patientID: uuidv4() } };
        case 'appointment-patients-select-patient-action':
          return { name: 'patient-info-state', props: action.props };
        case 'appointment-patients-continue-action':
          return { name: 'covid-19-for-us-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'patient-info-state':
      switch (action.name) {
        case 'patient-info-continue-action': {
          const encounter = context.encounters.find((e) => e.id === action.props.encounterID);
          const encounterProductTypes = encounter?.productTypes ?? [];

          const doesPatientHaveMedicare = encounter?.isMedicare;
          const hasLabProductType = encounterProductTypes.includes(ProductType.LAB_TEST) === true;
          const hasCOVID19ProductType =
            encounterProductTypes.includes(ProductType.RAPID_COVID_TEST) === true;

          // With Lab test & patient being Medicare, navigate to Medicare flow
          if (action.props.isPatient && hasLabProductType && doesPatientHaveMedicare)
            return { name: 'medicare-screener-state', props: action.props };

          // With a Lab test, we always start with the Lab flow
          if (
            action.props.isPatient &&
            hasLabProductType &&
            getPartnerConfig(context.appointment.partnerOrganization).insuranceUploadFromPatient !==
              'skip'
          )
            return { name: 'insurance-photo-state', props: action.props };

          // With a partner that skips insurance but wants requisition, we want requisition upload next
          if (
            action.props.isPatient &&
            hasLabProductType &&
            getPartnerConfig(context.appointment.partnerOrganization)
              .requisitionUploadFromPatient !== 'skip'
          )
            return { name: 'requisition-state', props: action.props };

          // With a COVID-19 test and no Lab test, we go to the COVID-19 flow
          if (action.props.isPatient && hasCOVID19ProductType)
            return { name: 'encounter-covid-19-questions-state', props: action.props };

          // With no product types we have no specific steps to take
          return {
            name:
              context.patientsAmount === 'single' && action.props.isPatient
                ? 'covid-19-for-us-state'
                : 'appointment-patients-state',
          };
        }

        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'insurance-photo-state':
      switch (action.name) {
        case 'insurance-photo-continue-action':
          if (
            getPartnerConfig(context.appointment.partnerOrganization)
              .requisitionUploadFromPatient !== 'skip'
          )
            return { name: 'requisition-state', props: action.wizardState.props };

          return {
            name:
              context.patientsAmount === 'single'
                ? 'covid-19-for-us-state'
                : 'appointment-patients-state',
          };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'medicare-screener-state':
      switch (action.name) {
        case 'medicare-screener-continue-action':
          return { name: 'medicare-eligibility-status-state', props: action.props };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'medicare-eligibility-status-state':
      switch (action.name) {
        case 'medicare-eligibility-status-reset-multiple-action':
          return { name: 'appointment-patients-state', props: action.wizardState.props };
        case 'medicare-eligibility-status-continue-action':
          return { name: 'medicare-photo-state', props: action.props };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'medicare-photo-state':
      switch (action.name) {
        case 'medicare-photo-continue-action':
          return { name: 'medicare-insurance-id-state', props: action.props };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'medicare-insurance-id-state':
      switch (action.name) {
        case 'medicare-insurance-id-continue-action': {
          const encounter = context.encounters.find((e) => e.id === action.props.encounterID);

          const encounterProductTypes = encounter?.productTypes ?? [];
          const hasLabProductType = encounterProductTypes.includes(ProductType.LAB_TEST) === true;
          const hasCOVID19ProductType =
            encounterProductTypes.includes(ProductType.RAPID_COVID_TEST) === true;

          // TODO (dhruv) - same code exists in patient-info-state (i.e. DRY)
          // Once we reach the medicare id continue action, we continue with remaining flow
          // With a Lab test, we always start with the Lab flow
          if (
            action.props.isPatient &&
            hasLabProductType &&
            getPartnerConfig(context.appointment.partnerOrganization).insuranceUploadFromPatient !==
              'skip'
          )
            return { name: 'insurance-photo-state', props: action.props };

          // With a partner that skips insurance but wants requisition, we want requisition upload next
          if (
            action.props.isPatient &&
            hasLabProductType &&
            getPartnerConfig(context.appointment.partnerOrganization)
              .requisitionUploadFromPatient !== 'skip'
          )
            return { name: 'requisition-state', props: action.props };

          // With a COVID-19 test and no Lab test, we go to the COVID-19 flow
          if (action.props.isPatient && hasCOVID19ProductType)
            return { name: 'encounter-covid-19-questions-state', props: action.props };

          // With no product types we have no specific steps to take
          return {
            name:
              context.patientsAmount === 'single' && action.props.isPatient
                ? 'covid-19-for-us-state'
                : 'appointment-patients-state',
          };
        }
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'requisition-state':
      switch (action.name) {
        case 'requisition-continue-action': {
          const { encounterID } = action.props;

          if (
            encounterID != null &&
            context.encounters != null &&
            (context.encounters
              .find((e) => e.id === encounterID)
              ?.productTypes?.includes(ProductType.RAPID_COVID_TEST) ??
              false)
          )
            return { name: 'encounter-covid-19-questions-state', props: action.props };

          return {
            name:
              context.patientsAmount === 'single'
                ? 'covid-19-for-us-state'
                : 'appointment-patients-state',
            props: action.wizardState.props,
          };
        }

        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'encounter-covid-19-questions-state':
      switch (action.name) {
        case 'encounter-covid-19-questions-continue-action':
          return {
            name:
              context.patientsAmount === 'single'
                ? 'covid-19-for-us-state'
                : 'appointment-patients-state',
          };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'covid-19-for-us-state':
      switch (action.name) {
        case 'covid-19-for-us-continue-action':
          return { name: 'appointment-notes-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-notes-state':
      switch (action.name) {
        case 'appointment-notes-continue-action':
          return { name: 'appointment-prep-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-prep-state':
      switch (action.name) {
        case 'appointment-prep-continue-action':
          return { name: 'appointment-billing-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'appointment-billing-state':
      switch (action.name) {
        case 'appointment-billing-continue-action':
          return { name: 'youre-booked-state' };
        case 'choose-another-time-action':
          return { name: 'choose-new-time-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    case 'choose-new-time-state':
      switch (action.name) {
        case 'choose-new-time-continue-action':
          return { name: 'appointment-billing-state' };
        default:
          throw new UnexpectedWizardActionError(action);
      }

    default:
      throw new UnexpectedWizardActionError(action);
  }
}

function getLoggerFields(context: BookingWizardContext): { [field: string]: unknown } {
  const productTypeCounts = Object.fromEntries(Object.values(ProductType).map((t) => [t, 0]));
  for (const encounter of Object.values(context?.encounters))
    if (encounter.productTypes != null) {
      for (const productType of encounter.productTypes) ++productTypeCounts[productType];
    }

  const { location } = context.appointment;
  const partnerConfig = getPartnerConfig(context.appointment.partnerOrganization);

  return {
    partnerOrganization: context.appointment.partnerOrganization,
    payer: partnerConfig.payer,
    partnerVertical: partnerConfig.vertical,
    coordinates: location && [location.position?.latitude, location.position?.longitude].join(),
    initialPatientCount: context.patientsAmount,
    initialProductType: context.primaryProductType,
    isCoordinatorAPatient: context.coordinatorIsPatient,
    location,
    patientCount: context.encounters.length,
    productTypeCounts,
    serviceRegionName: findActiveServiceRegionOrNull(
      location?.address?.postalCode,
      context.appointment.partnerOrganization,
    ),
    metroName: findMetroOrNull(
      findActiveServiceRegionOrNull(
        location?.address?.postalCode,
        context.appointment.partnerOrganization,
      ),
    ),
  };
}

function getDefaultContext(): BookingWizardContext {
  return {
    outstandingRequisitions: [],
    appointment: {},
    encounters: [],
    patients: [],
    primaryProductType: ProductType.LAB_TEST,
    patientsAmount: 'single',
    coordinatorIsPatient: true,
  };
}

export const BOOKING_WIZARD: Wizard<
  BookingWizardState,
  BookingWizardAction,
  BookingWizardContext,
  null
> = {
  name: 'booking',
  defaultState: {
    name: 'booking-redirect-state',
  },
  getDefaultContext,
  handleAction,
  getLoggerFields,
};
